import React from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Field, Form as FinalForm, FormSpy, useForm, useFormState } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { Form, RangeSlider } from '../../../components';
import css from './PriceFilterForm.module.css';

const DEBOUNCE_WAIT_TIME = 400;

// Utility to format number with commas
const formatNumber = value => {
  if (value === '' || value == null) return '';
  const num = Number(value);
  return isNaN(num) ? '' : num.toLocaleString();
};

// Utility to strip commas and parse to int
const parseNumber = value => {
  if (typeof value === 'string') {
    const raw = value.replace(/,/g, '');
    const num = parseInt(raw, 10);
    return isNaN(num) ? '' : num;
  }
  return value;
};

// Existing min/max boundary parsing
const parseMin = (min, currentMax) => value => {
  const parsedValue = parseNumber(value);
  if (parsedValue === '') return '';
  return Math.max(parsedValue, min);
};

const parseMax = (max, currentMin) => value => {
  const parsedValue = parseNumber(value);
  if (parsedValue === '') return '';
  return Math.min(parsedValue, max);
};

export const PriceFilterFields = props => {
  const {
    id,
    min = 0,
    max = 1000,
    step = 1,
    showAsPopup = false,
    isOpen = false,
    isInSideBar = false,
    hideActions = false,
    style,
    className,
    contentRef,
    liveEdit,
    onClear,
    onCancel,
    children,
    handleSubmit,
    handleChange,
    currency,
  } = props;

  const form = useForm();
  const { values } = useFormState();
  const intl = useIntl();

  const minPriceRaw = values.minPrice;
  const maxPriceRaw = values.maxPrice;
  const minPrice = typeof minPriceRaw !== 'string' ? minPriceRaw : min;
  const maxPrice = typeof maxPriceRaw !== 'string' ? maxPriceRaw : max;

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const clearText = intl.formatMessage({ id: 'PriceFilterForm.reset' });
  const cancelText = intl.formatMessage({ id: 'PriceFilterForm.cancel' });
  const submitText = intl.formatMessage({ id: 'PriceFilterForm.submit' });

  const classes = classNames(css.root, className, {
    [css.popup]: showAsPopup,
    [css.isOpenAsPopup]: showAsPopup && isOpen,
    [css.plain]: !showAsPopup,
    [css.isOpen]: !showAsPopup && isOpen,
  });

  return (
    <div className={classes} style={{ minWidth: '330px', ...style }} ref={contentRef}>
      <div className={css.contentWrapper}>
        {showAsPopup && (
          <span className={css.label}>
            <FormattedMessage id="PriceFilterForm.label" />
          </span>
        )}
        <div className={css.inputsWrapper}>
          <Field
            className={classNames(css.minPrice, { [css.priceInSidebar]: isInSideBar })}
            id={`${id}.minPrice`}
            name="minPrice"
            parse={parseMin(min, maxPrice)}
            format={formatNumber}
          >
            {({ input }) => (
              <input
                {...input}
                type="text"
                placeholder={formatNumber(min)}
                step={step}
                onChange={e => input.onChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && hideActions) {
                    e.preventDefault();
                  }
                }}
              />
            )}
          </Field>
          <span className={css.priceSeparator}>-</span>
          <Field
            className={classNames(css.maxPrice, { [css.priceInSideBar]: isInSideBar })}
            id={`${id}.maxPrice`}
            name="maxPrice"
            parse={parseMax(max, minPrice)}
            format={formatNumber}
          >
            {({ input }) => (
              <input
                {...input}
                type="text"
                placeholder={formatNumber(max)}
                step={step}
                onChange={e => input.onChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && hideActions) {
                    e.preventDefault();
                  }
                }}
              />
            )}
          </Field>
          {currency}
        </div>
      </div>

      <div className={css.sliderWrapper}>
        <RangeSlider
          min={min}
          max={max}
          step={step}
          handles={[minPrice, maxPrice]}
          onChange={([minVal, maxVal]) => {
            form.change('minPrice', minVal);
            form.change('maxPrice', maxVal);
          }}
        />
      </div>

      {liveEdit ? (
        <FormSpy onChange={handleChange} subscription={{ values: true, dirty: true }} />
      ) : !hideActions ? (
        <div className={css.buttonsWrapper}>
          <button className={css.clearButton} type="button" onClick={onClear}>
            {clearText}
          </button>
          <button className={css.cancelButton} type="button" onClick={handleCancel}>
            {cancelText}
          </button>
          <button className={css.submitButton} type="submit">
            {submitText}
          </button>
        </div>
      ) : null}

      {children}
    </div>
  );
};

const PriceFilterForm = props => {
  const {
    liveEdit = false,
    onChange,
    onSubmit,
    onCancel,
    onClear,
    standalone = true,
    hideActions = false,
    intl: injectedIntl,
    ...rest
  } = props;

  const intl = injectedIntl || useIntl();

  if (liveEdit && !onChange) {
    throw new Error('PriceFilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'PriceFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  const handleChange = debounce(formState => {
    if (formState.dirty) {
      const { minPrice, maxPrice, ...restValues } = formState.values;
      onChange({
        minPrice: minPrice === '' ? rest.min : minPrice,
        maxPrice: maxPrice === '' ? rest.max : maxPrice,
        ...restValues,
      });
    }
  }, DEBOUNCE_WAIT_TIME);

  const handleSubmit = values => {
    const { minPrice, maxPrice, ...restValues } = values;
    return onSubmit({
      minPrice: minPrice === '' ? rest.min : minPrice,
      maxPrice: maxPrice === '' ? rest.max : maxPrice,
      ...restValues,
    });
  };

  if (!standalone) {
    return (
      <PriceFilterFields
        {...props}
        intl={intl}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
    );
  }

  return (
    <FinalForm
      {...rest}
      onSubmit={liveEdit ? () => null : handleSubmit}
      render={formRenderProps => (
        <Form onSubmit={formRenderProps.handleSubmit}>
          <PriceFilterFields
            {...props}
            {...formRenderProps}
            intl={intl}
            handleChange={handleChange}
            handleSubmit={formRenderProps.handleSubmit}
          />
        </Form>
      )}
    />
  );
};

export default PriceFilterForm;
